import { useEffect, useState } from 'react'
import EmptyState from '../shell/EmptyState'
import AssignmentForm from './AssignmentForm'
import RetireCatalogEntryForm from './RetireCatalogEntryForm'
import type { AccountTeam } from '../../pages/admin/accountStore'
import {
  createAssignment,
  getAssignments,
  updateAssignment,
  type Activity,
  type Assignment,
  type AssignmentValues,
  type Qualification,
} from '../../pages/admin/catalogStore'

type StatusFilter = 'all' | 'active' | 'retired'

const STATUS_FILTERS: { key: StatusFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'retired', label: 'Retired' },
]

// The one form open at a time: creating an assignment, or acting on one row.
type Panel = { kind: 'create' } | { kind: 'edit' | 'retire'; id: string }

const ACTION =
  'flex min-h-11 items-center justify-center rounded-control border border-line-strong px-3 text-small font-bold hover:bg-surface-2'

/**
 * docs/product/scheduling-model.md's assignments: "a name, a duty category, a
 * team or location, a default activity, a sort priority, an optional required
 * qualification, and an active flag." Every assignment with those, and forms to
 * create one, change everything but its code and name, and retire or restore it.
 */
export default function AssignmentsSection({
  teams,
  activities,
  qualifications,
}: {
  teams: AccountTeam[]
  activities: Activity[]
  qualifications: Qualification[]
}) {
  const [assignments, setAssignments] = useState<Assignment[]>([])
  const [loaded, setLoaded] = useState(false)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [panel, setPanel] = useState<Panel | null>(null)
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    getAssignments().then((loadedAssignments) => {
      if (cancelled) return
      setAssignments(loadedAssignments)
      setLoaded(true)
    })
    return () => {
      cancelled = true
    }
  }, [])

  const activityPaths = new Map(activities.map((activity) => [activity.id, activity.path]))
  const panelAssignment = panel && panel.kind !== 'create' ? (assignments.find((assignment) => assignment.id === panel.id) ?? null) : null
  const query = search.trim().toLowerCase()
  const filtered = assignments.filter((assignment) => {
    if (statusFilter === 'active' && !assignment.active) return false
    if (statusFilter === 'retired' && assignment.active) return false
    return !query || `${assignment.name} ${assignment.code} ${assignment.dutyCategory} ${assignment.teamName}`.toLowerCase().includes(query)
  })

  function openPanel(next: Panel) {
    setNotice(null)
    setFormError(null)
    setPanel(next)
  }

  function closePanel() {
    setPanel(null)
    setFormError(null)
  }

  async function run(action: () => Promise<{ ok: true } | { ok: false; message: string }>, done: string) {
    setSaving(true)
    setFormError(null)
    const result = await action()
    setSaving(false)
    if (!result.ok) {
      setFormError(result.message)
      return
    }
    setAssignments(await getAssignments())
    setPanel(null)
    setNotice(done)
  }

  const valuesOf = (assignment: Assignment): AssignmentValues => ({
    dutyCategory: assignment.dutyCategory,
    teamId: assignment.teamId,
    sortPriority: assignment.sortPriority,
    requiredQualificationId: assignment.requiredQualificationId,
    defaultActivityId: assignment.defaultActivityId,
  })

  const create = (values: AssignmentValues & { code: string; name: string }) => run(() => createAssignment(values), `Created ${values.name}.`)
  const save = (values: AssignmentValues) =>
    panelAssignment ? run(() => updateAssignment(panelAssignment.id, values, panelAssignment.active), `Updated ${panelAssignment.name}.`) : Promise.resolve()
  const retire = () =>
    panelAssignment ? run(() => updateAssignment(panelAssignment.id, valuesOf(panelAssignment), false), `Retired ${panelAssignment.name}.`) : Promise.resolve()
  const restore = (assignment: Assignment) => {
    setNotice(null)
    return run(() => updateAssignment(assignment.id, valuesOf(assignment), true), `Restored ${assignment.name}.`)
  }

  const usage = (assignment: Assignment) =>
    assignment.entries + assignment.targets > 0
      ? ` ${assignment.entries} schedule ${assignment.entries === 1 ? 'entry' : 'entries'} and ${assignment.targets} staffing ${assignment.targets === 1 ? 'target' : 'targets'} that name it keep reading it.`
      : ' Nothing uses it yet.'

  return (
    <section id="assignments" aria-labelledby="assignments-heading" className="flex flex-col gap-4">
      <div>
        <h2 id="assignments-heading" className="text-h3 font-extrabold">
          Assignments
        </h2>
        <p className="mt-1 text-small text-ink-muted">What a workday is spent on. The staffing target form and the daily assignment board offer the active ones.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-surface border border-line bg-canvas p-3">
        <label htmlFor="assignment-search" className="sr-only">
          Search assignments
        </label>
        <input
          id="assignment-search"
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search by name, code, duty category, or team"
          className="min-h-11 min-w-[240px] flex-1 rounded-control border border-line-strong px-3 text-small"
        />
        <div role="group" aria-label="Filter assignments by status" className="flex gap-2">
          {STATUS_FILTERS.map((option) => (
            <button
              key={option.key}
              type="button"
              aria-pressed={statusFilter === option.key}
              onClick={() => setStatusFilter(option.key)}
              className={`flex min-h-11 items-center justify-center rounded-control border px-3 text-small font-bold ${
                statusFilter === option.key ? 'border-primary bg-selected' : 'border-line-strong hover:bg-surface-2'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          aria-expanded={panel?.kind === 'create'}
          onClick={() => openPanel({ kind: 'create' })}
          className="flex min-h-11 items-center justify-center rounded-control bg-primary px-4 text-small font-bold text-primary-ink hover:bg-primary-hover"
        >
          Create assignment
        </button>
      </div>

      {notice && (
        <p role="status" className="text-small font-semibold text-success">
          {notice}
        </p>
      )}

      {formError && !panel && (
        <p role="alert" className="text-small font-semibold text-error">
          {formError}
        </p>
      )}

      {panel?.kind === 'create' && (
        <AssignmentForm teams={teams} activities={activities} qualifications={qualifications} saving={saving} error={formError} onCreate={create} onSave={save} onCancel={closePanel} />
      )}
      {panel?.kind === 'edit' && panelAssignment && (
        <AssignmentForm
          key={panelAssignment.id}
          assignment={panelAssignment}
          teams={teams}
          activities={activities}
          qualifications={qualifications}
          saving={saving}
          error={formError}
          onCreate={create}
          onSave={save}
          onCancel={closePanel}
        />
      )}
      {panel?.kind === 'retire' && panelAssignment && (
        <RetireCatalogEntryForm
          key={panelAssignment.id}
          name={panelAssignment.name}
          message={`Retiring ${panelAssignment.name} takes it out of the assignment pickers.${usage(panelAssignment)}`}
          confirmLabel="Retire assignment"
          saving={saving}
          error={formError}
          onConfirm={retire}
          onCancel={closePanel}
        />
      )}

      {!loaded ? (
        <p role="status" className="text-small text-ink-muted">
          Loading&hellip;
        </p>
      ) : filtered.length === 0 ? (
        <EmptyState>No assignments match your search.</EmptyState>
      ) : (
        <div className="overflow-x-auto rounded-surface border border-line">
          <table aria-label="Assignments" className="w-full border-collapse">
            <thead>
              <tr>
                {['Assignment', 'Duty category', 'Team', 'Priority', 'Qualification', 'Default activity', 'Status'].map((heading) => (
                  <th
                    key={heading}
                    scope="col"
                    className="border-b border-line bg-surface-2 px-3 py-2 text-left text-caption font-bold tracking-wide whitespace-nowrap text-ink-subtle uppercase"
                  >
                    {heading}
                  </th>
                ))}
                <th scope="col" className="border-b border-line bg-surface-2 py-2 pr-2 pl-3 text-right">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((assignment) => (
                <tr key={assignment.id}>
                  <th scope="row" className="border-b border-line px-3 py-2 text-left text-small font-normal">
                    <span className="block font-bold">{assignment.name}</span>
                    <span className="block text-caption text-ink-muted">{assignment.code}</span>
                  </th>
                  <td className="border-b border-line px-3 py-2 text-small">{assignment.dutyCategory}</td>
                  <td className="border-b border-line px-3 py-2 text-small">{assignment.teamId ? assignment.teamName : 'Office-wide'}</td>
                  <td className="border-b border-line px-3 py-2 text-small text-ink-muted [font-variant-numeric:tabular-nums]">{assignment.sortPriority}</td>
                  <td className="border-b border-line px-3 py-2 text-small">{assignment.qualificationName || 'None'}</td>
                  <td className="border-b border-line px-3 py-2 text-small">{assignment.defaultActivityId ? (activityPaths.get(assignment.defaultActivityId) ?? '') : 'None'}</td>
                  <td className="border-b border-line px-3 py-2 text-small whitespace-nowrap">
                    <span
                      className={`rounded-full px-2 py-0.5 text-caption font-bold ${
                        assignment.active ? 'bg-success/10 text-success' : 'bg-surface-2 text-ink-muted'
                      }`}
                    >
                      {assignment.active ? 'Active' : 'Retired'}
                    </span>
                  </td>
                  <td className="border-b border-line py-2 pr-2 pl-3 text-small">
                    {/* Each button has its own fixed slot, so Edit and the retire
                        or restore button line up in every row. */}
                    <div className="flex justify-end gap-2 whitespace-nowrap">
                      <div className="w-14">
                        <button type="button" aria-label={`Edit ${assignment.name}`} onClick={() => openPanel({ kind: 'edit', id: assignment.id })} className={`${ACTION} w-full`}>
                          Edit
                        </button>
                      </div>
                      <div className="w-24">
                        {assignment.active ? (
                          <button type="button" aria-label={`Retire ${assignment.name}`} onClick={() => openPanel({ kind: 'retire', id: assignment.id })} className={`${ACTION} w-full`}>
                            Retire
                          </button>
                        ) : (
                          <button type="button" aria-label={`Restore ${assignment.name}`} disabled={saving} onClick={() => void restore(assignment)} className={`${ACTION} w-full disabled:opacity-40`}>
                            Restore
                          </button>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
